/**
 * InsightBrowserTable Component
 *
 * Paginated table for browsing insights:
 * - Sortable columns
 * - Checkbox for bulk selection
 * - Row click opens detail modal
 * - Pagination controls
 */

import { useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  Sparkles,
} from 'lucide-react';

function SortHeader({ column, label, sortBy, sortOrder, onSort }) {
  const isActive = sortBy === column;

  return (
    <button
      onClick={() => onSort(column)}
      className="flex items-center gap-1 text-left hover:text-white transition-colors"
    >
      {label}
      {isActive && (
        sortOrder === 'asc' ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )
      )}
    </button>
  );
}

function TypeBadge({ type }) {
  const colors = {
    'bug-fix': '#ef4444',
    'pattern': '#10b981',
    'preference': '#f59e0b',
    'learning': '#6366f1',
    'decision': '#8b5cf6',
  };
  const color = colors[type] || '#6b7280';

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
      style={{
        backgroundColor: `${color}20`,
        color: color,
        border: `1px solid ${color}40`,
      }}
    >
      {type}
    </span>
  );
}

export default function InsightBrowserTable({
  insights,
  pagination,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  selectedIds,
  onSelectionChange,
  onRowClick,
  isLoading,
}) {
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onSelectionChange(insights.map((i) => i.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectRow = (id, e) => {
    e.stopPropagation();
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const allSelected = insights.length > 0 && insights.every((i) => selectedIds.includes(i.id));
  const someSelected = insights.some((i) => selectedIds.includes(i.id));

  if (isLoading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 text-center">
        <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">No insights found</p>
        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-900/50 border-b border-gray-700">
            <tr className="text-xs text-gray-400 uppercase tracking-wider">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected && !allSelected)}
                  onChange={handleSelectAll}
                  className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <SortHeader
                  column="type"
                  label="Type"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              </th>
              <th className="px-4 py-3 text-left">Content</th>
              <th className="px-4 py-3 text-left">
                <SortHeader
                  column="agent"
                  label="Agent"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              </th>
              <th className="px-4 py-3 text-left">Project</th>
              <th className="px-4 py-3 text-left w-10">
                <Sparkles className="w-4 h-4" title="Has Embedding" />
              </th>
              <th className="px-4 py-3 text-left">
                <SortHeader
                  column="createdAt"
                  label="Created"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {insights.map((insight) => (
              <tr
                key={insight.id}
                onClick={() => onRowClick(insight)}
                className={`
                  cursor-pointer transition-colors
                  ${selectedIds.includes(insight.id) ? 'bg-indigo-500/10' : 'hover:bg-gray-700/30'}
                `}
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(insight.id)}
                    onChange={(e) => handleSelectRow(insight.id, e)}
                    className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                  />
                </td>
                <td className="px-4 py-3">
                  <TypeBadge type={insight.type} />
                </td>
                <td className="px-4 py-3 max-w-md">
                  <p className="text-sm text-gray-300 truncate">
                    {insight.content?.substring(0, 100)}
                    {insight.content?.length > 100 && '...'}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-400">
                    {insight.agentName || insight.agent?.displayName || insight.agent?.name || '-'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-400">
                    {insight.projectName || insight.project?.displayName || insight.project?.name || '-'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {insight.hasEmbedding || insight.embedding ? (
                    <Sparkles className="w-4 h-4 text-amber-400" />
                  ) : (
                    <span className="text-gray-600">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {insight.createdAt
                    ? new Date(insight.createdAt).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} insights
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 text-gray-300" />
          </button>
          <span className="text-sm text-gray-400">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </button>
        </div>
      </div>
    </div>
  );
}
